import { useEffect, useState } from "react";
import { fetchCustomerOrders } from "../api/customerOrders";
import { logoutCustomer } from "../api/customerAuth";
import StoreBoot from "../components/StoreBoot";
import StoreHeader from "../components/StoreHeader";
import {
  clearCustomerSessionToken,
  getCustomerSessionToken,
} from "../lib/customerSession";
import { storePath } from "../lib/storePaths";

const FULFILLMENT_STATUS_LABELS = {
  pending: "Pendente",
  confirmed: "Confirmado",
  processing: "Em processamento",
  dispatched: "Despachado",
  on_hold: "Em espera",
  fulfilled: "Atendido",
  failed: "Falhou",
  refunded: "Reembolsado",
  delivered: "Entregue",
};

function formatMoney(value, currency) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: currency || "BRL",
  }).format(Number(value || 0) / 100);
}

function formatDate(value) {
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
}

export default function CustomerDashboardPage({ storeSlug }) {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [loggingOut, setLoggingOut] = useState(false);
  const loginUrl = storePath("/login", storeSlug);
  const storeUrl = storePath("", storeSlug);

  useEffect(() => {
    const sessionToken = getCustomerSessionToken(storeSlug);
    if (!sessionToken) {
      window.location.replace(loginUrl);
      return undefined;
    }

    const controller = new AbortController();
    fetchCustomerOrders(storeSlug, sessionToken, {
      signal: controller.signal,
    })
      .then((result) => {
        setDashboard(result);
        document.title = `Meus pedidos | ${result.store.name}`;
      })
      .catch((requestError) => {
        if (requestError.name === "AbortError") return;
        if (requestError.status === 401) {
          clearCustomerSessionToken(storeSlug);
          window.location.replace(loginUrl);
          return;
        }
        setError("Não foi possível carregar seus pedidos. Tente novamente.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [loginUrl, storeSlug]);

  async function handleLogout() {
    if (loggingOut) return;
    setLoggingOut(true);
    const sessionToken = getCustomerSessionToken(storeSlug);
    if (sessionToken) {
      await logoutCustomer(storeSlug, sessionToken).catch(() => {});
    }
    clearCustomerSessionToken(storeSlug);
    window.location.assign(loginUrl);
  }

  if (loading && !dashboard) return <StoreBoot />;

  const orders = dashboard?.orders || [];

  return (
    <div className="storefront-shell">
      <StoreHeader store={dashboard?.store} storeSlug={storeSlug} />
      <main className="customer-dashboard">
        <header className="customer-dashboard__heading">
          <div>
            <p className="customer-dashboard__eyebrow">Área do cliente</p>
            <h1>Meus pedidos</h1>
            <p>
              {dashboard?.customer?.name
                ? `Olá, ${dashboard.customer.name}.`
                : "Acompanhe suas compras nesta loja."}
            </p>
          </div>
          <button type="button" onClick={handleLogout} disabled={loggingOut}>
            {loggingOut ? "Saindo..." : "Sair"}
          </button>
        </header>

        {loading && (
          <div className="customer-dashboard__state">Carregando pedidos...</div>
        )}
        {!loading && error && (
          <div className="customer-dashboard__state customer-dashboard__state--error">
            <p>{error}</p>
            <button type="button" onClick={() => window.location.reload()}>
              Tentar novamente
            </button>
          </div>
        )}
        {!loading && !error && orders.length === 0 && (
          <div className="customer-dashboard__state">
            <h2>Nenhum pedido encontrado</h2>
            <p>Quando você fizer uma compra, ela aparecerá aqui.</p>
            <a href={storeUrl}>Ver produtos</a>
          </div>
        )}

        {!loading && !error && orders.length > 0 && (
          <div className="customer-orders-table-wrap">
            <table className="customer-orders-table">
              <thead>
                <tr>
                  <th>Pedido</th>
                  <th>Data</th>
                  <th>Itens</th>
                  <th>Status</th>
                  <th>Valor</th>
                  <th aria-label="Abrir pedido" />
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => {
                  const detailUrl = storePath(
                    `/dashboard/orders/${encodeURIComponent(order.id)}`,
                    storeSlug
                  );
                  const openOrder = () => window.location.assign(detailUrl);
                  return (
                    <tr
                      key={order.id}
                      role="link"
                      tabIndex={0}
                      onClick={openOrder}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          openOrder();
                        }
                      }}
                    >
                      <td>
                        <strong>{order.id}</strong>
                      </td>
                      <td>
                        <time dateTime={order.paidAt || order.createdAt}>
                          {formatDate(order.paidAt || order.createdAt)}
                        </time>
                      </td>
                      <td>
                        {order.itemCount}{" "}
                        {order.itemCount === 1 ? "item" : "itens"}
                      </td>
                      <td>
                        <span
                          className={`order-status order-status--${order.fulfillmentStatus}`}
                        >
                          {FULFILLMENT_STATUS_LABELS[
                            order.fulfillmentStatus
                          ] ||
                            order.fulfillmentStatus ||
                            "Pendente"}
                        </span>
                        {order.trackingNumber && (
                          <small className="customer-order-tracking">
                            {order.trackingNumber}
                          </small>
                        )}
                      </td>
                      <td>
                        <strong>
                          {formatMoney(order.totalMinor, order.currency)}
                        </strong>
                      </td>
                      <td className="customer-orders-table__arrow" aria-hidden>
                        →
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
